import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Sparkles, Sun, Moon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { OFFICIAL_CREATE_URL } from '@/lib/constants';

interface HeaderProps {
  variant?: 'default' | 'auth';
}

export const Header = ({ variant = 'default' }: HeaderProps) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [ripple, setRipple] = useState<{ active: boolean; x: number; y: number; toLight: boolean }>({ 
    active: false, x: 0, y: 0, toLight: false 
  });

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Play subtle click sound
  const playToggleSound = (toLight: boolean) => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies for light/dark
      oscillator.frequency.setValueAtTime(toLight ? 800 : 600, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(toLight ? 1200 : 400, audioContext.currentTime + 0.08);
      
      oscillator.type = 'sine';
      
      // Very subtle volume
      gainNode.gain.setValueAtTime(0.03, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Audio not supported, fail silently
    }
  };

  const handleThemeToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const toLight = theme === 'dark';
    
    // Play sound
    playToggleSound(toLight);
    
    setRipple({ active: true, x, y, toLight });
    
    // Change theme after a small delay to let ripple start
    setTimeout(() => {
      setTheme(toLight ? 'light' : 'dark');
    }, 50);
    
    // Remove ripple after animation
    setTimeout(() => {
      setRipple(prev => ({ ...prev, active: false }));
    }, 600);
  };

  return (
    <>
      {/* Theme Ripple Effect */}
      {ripple.active && (
        <div
          className="fixed inset-0 pointer-events-none z-[9999]"
          style={{
            background: ripple.toLight 
              ? 'radial-gradient(circle at var(--ripple-x) var(--ripple-y), rgba(255,255,255,0.9) 0%, transparent 70%)'
              : 'radial-gradient(circle at var(--ripple-x) var(--ripple-y), rgba(18,18,18,0.9) 0%, transparent 70%)',
            ['--ripple-x' as any]: `${ripple.x}px`,
            ['--ripple-y' as any]: `${ripple.y}px`,
            animation: 'theme-ripple 0.6s ease-out forwards',
          }}
        />
      )}
      <style>{`
        @keyframes theme-ripple {
          0% {
            opacity: 1;
            transform: scale(0);
          }
          50% {
            opacity: 0.8;
          }
          100% {
            opacity: 0;
            transform: scale(4);
          }
        }
      `}</style>
      <header className="sticky top-0 z-50 glass-strong soft-shadow-sm border-b border-border/30">
      <div className="py-4 px-6 lg:px-8 flex items-center justify-between max-w-7xl mx-auto">
        <div 
          className="flex items-center gap-2 sm:gap-3 cursor-pointer" 
          onClick={() => navigate('/')}
        >
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-2xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center soft-shadow">
            <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="white" />
          </div>
          <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-primary to-primary-dark bg-clip-text text-transparent">
            QR Portrait
          </span>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Theme Toggle Button */}
          {mounted && (
            <button
              onClick={handleThemeToggle}
              className="relative w-6 h-6 rounded-full bg-secondary/80 hover:bg-secondary border border-border/50 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95 overflow-hidden"
              aria-label="Toggle theme"
            >
              <div className="relative w-3 h-3">
                <Sun 
                  className={`absolute inset-0 w-3 h-3 text-amber-500 transition-all duration-500 ${
                    theme === 'dark' 
                      ? 'rotate-0 scale-100 opacity-100' 
                      : 'rotate-90 scale-0 opacity-0'
                  }`}
                />
                <Moon 
                  className={`absolute inset-0 w-3 h-3 text-primary transition-all duration-500 ${
                    theme === 'dark' 
                      ? '-rotate-90 scale-0 opacity-0' 
                      : 'rotate-0 scale-100 opacity-100'
                  }`}
                />
              </div>
            </button>
          )}
          {variant === 'auth' ? (
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')} 
              className="hidden sm:flex"
            >
              Retour à l'accueil
            </Button>
          ) : (
            <Button 
              variant="premium" 
              onClick={() => navigate(OFFICIAL_CREATE_URL)} 
              className="gap-1 sm:gap-2 text-sm sm:text-base px-3 sm:px-4"
            >
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Commencer</span>
            </Button>
          )}
        </div>
      </div>
    </header>
    </>
  );
};
